import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { CATEGORIES as SEED_CATEGORIES, SERVICES as SEED_SERVICES, OFFERS as SEED_OFFERS } from '../mock/data';
import { api, setToken } from '../services/api';

const AppContext = createContext(null);

const load = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
};

// Convert flat services list from API to keyed dict by category_id
const reshapeServices = (list, categories) => {
  const out = {};
  for (const c of categories) out[c.id] = { title: c.name, items: [] };
  for (const s of list) {
    if (!out[s.category_id]) out[s.category_id] = { title: s.category_id, items: [] };
    out[s.category_id].items.push(s);
  }
  return out;
};

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(() => load('c24_user', null));
  const [cart, setCart] = useState(() => load('c24_cart', []));
  const [bookings, setBookings] = useState([]);
  const [address, setAddress] = useState(() => localStorage.getItem('c24_address') || 'Marine Drive, Ernakulam');
  const [role, setRole] = useState(() => localStorage.getItem('c24_role') || 'customer');

  const [categories, setCategories] = useState(SEED_CATEGORIES);
  const [services, setServices] = useState(SEED_SERVICES);
  const [offers, setOffers] = useState(SEED_OFFERS);
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState(null); // { tab_key: {view,edit,delete} }

  const refreshPermissions = useCallback(async () => {
    try {
      const tok = localStorage.getItem('c24_token');
      if (!tok) { setPermissions(null); return; }
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/permissions`, { headers: { Authorization: `Bearer ${tok}` } });
      if (!r.ok) { setPermissions(null); return; }
      const d = await r.json();
      setPermissions(d.resolved || {});
    } catch { setPermissions(null); }
  }, []);

  const hasPermission = useCallback((tabKey, action = 'view') => {
    // Admins bypass (extra safety); if permissions not loaded yet, allow view-only.
    if (user?.role === 'admin') return true;
    if (!permissions) return action === 'view';
    return !!(permissions[tabKey] && permissions[tabKey][action]);
  }, [permissions, user]);
  const [loading, setLoading] = useState(true);

  // Coupon + loyalty
  const [coupon, setCoupon] = useState(() => load('c24_coupon', null)); // { code, label }
  const [coins, setCoins] = useState(() => Number(localStorage.getItem('c24_coins')) || 500);
  const [coinsRedeem, setCoinsRedeem] = useState(0);

  useEffect(() => { localStorage.setItem('c24_user', JSON.stringify(user)); }, [user]);
  useEffect(() => { localStorage.setItem('c24_cart', JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem('c24_address', address); }, [address]);
  useEffect(() => { localStorage.setItem('c24_role', role); }, [role]);
  useEffect(() => { localStorage.setItem('c24_coupon', JSON.stringify(coupon)); }, [coupon]);
  useEffect(() => { localStorage.setItem('c24_coins', String(coins)); }, [coins]);

  // Sync rewards balance with backend whenever the user logs in
  useEffect(() => {
    if (!user) return;
    const tok = localStorage.getItem('c24_token');
    if (!tok) return;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rewards`, {
          headers: { Authorization: `Bearer ${tok}` },
          credentials: 'include',
        });
        if (res.ok) {
          const d = await res.json();
          setCoins(d.points || 0);
        }
      } catch {}
    })();
  }, [user]);

  const loadCatalog = useCallback(async () => {
    try {
      const [cats, offs] = await Promise.all([api.categories(), api.offers()]);
      // fetch services by iterating categories
      const allItems = [];
      for (const c of cats) {
        try {
          const { items } = await api.servicesByCategory(c.id);
          allItems.push(...items.map((it) => ({ ...it, category_id: c.id })));
        } catch (e) {}
      }
      setCategories(cats.length ? cats : SEED_CATEGORIES);
      setServices(reshapeServices(allItems, cats));
      setOffers(offs.length ? offs : SEED_OFFERS);
    } catch (e) {
      console.warn('Falling back to mock catalog', e?.message);
      setCategories(SEED_CATEGORIES);
      setServices(SEED_SERVICES);
      setOffers(SEED_OFFERS);
    }
  }, []);

  const refreshBookings = useCallback(async () => {
    try { const list = await api.myBookings(); setBookings(list); } catch { setBookings([]); }
  }, []);

  useEffect(() => { loadCatalog().finally(() => setLoading(false)); }, [loadCatalog]);
  useEffect(() => { if (user) refreshBookings(); else setBookings([]); }, [user, refreshBookings]);
  useEffect(() => { if (user) refreshPermissions(); else setPermissions(null); }, [user, refreshPermissions]);

  // ---- Auth ----
  const signup = async ({ name, email, password, phone, roleWanted, referral_code, district }) => {
    const res = await api.signup({ name, email, password, phone, role: roleWanted, referral_code, district });
    setToken(res.token);
    setUser(res.user);
    setRole(res.user.role);
    return res.user;
  };
  const login = async ({ email, password }) => {
    const res = await api.login({ email, password });
    setToken(res.token);
    setUser(res.user);
    setRole(res.user.role);
    return res.user;
  };
  const googleLogin = async (sessionId) => {
    const res = await api.googleSession(sessionId);
    setUser(res.user);
    setRole(res.user.role);
    return res.user;
  };
  const logout = async () => {
    try { await api.logout(); } catch {}
    setToken(null); setUser(null); setCart([]); setBookings([]);
  };

  // ---- Cart ----
  const addToCart = (item) => setCart((prev) => {
    const ex = prev.find((p) => p.id === item.id);
    return ex ? prev.map((p) => p.id === item.id ? { ...p, qty: p.qty + 1 } : p) : [...prev, { ...item, qty: 1 }];
  });
  const updateQty = (id, delta) => setCart((prev) => prev.map((p) => p.id === id ? { ...p, qty: p.qty + delta } : p).filter((p) => p.qty > 0));
  const removeFromCart = (id) => setCart((prev) => prev.filter((p) => p.id !== id));
  const clearCart = () => setCart([]);

  const placeBooking = async (data) => {
    const serviceItems = cart.filter((c) => c.type !== 'subscription');
    if (serviceItems.length === 0) return null;
    const payload = {
      items: serviceItems.map((c) => ({ id: c.id, name: c.name, price: c.price, qty: c.qty, image: c.image || '', hours: c.hours || null })),
      total: serviceItems.reduce((s, i) => s + i.price * i.qty, 0),
      date: data.date, slot: data.slot, payment: data.payment, address: data.address,
      wallet_paid: data.wallet_paid || 0,
    };
    const b = await api.createBooking(payload);
    setBookings((prev) => [b, ...prev]);
    clearCart();
    return b;
  };

  // ---- Admin CRUD (delegates to API and refreshes local catalog) ----
  const upsertCategory = async (cat) => { await api.upsertCategory(cat); await loadCatalog(); };
  const deleteCategory = async (id) => { await api.deleteCategory(id); await loadCatalog(); };
  const upsertService = async (categoryId, item) => { await api.upsertService({ ...item, category_id: categoryId }); await loadCatalog(); };
  const deleteService = async (categoryId, id) => { await api.deleteService(id); await loadCatalog(); };
  const upsertOffer = async (offer) => { await api.upsertOffer(offer); await loadCatalog(); };
  const deleteOffer = async (id) => { await api.deleteOffer(id); await loadCatalog(); };
  const resetAll = async () => { await loadCatalog(); };

  const registerUser = () => {}; // no-op: backend handles registration

  // ---- Coupons & Loyalty ----
  const COUPONS = {
    FLAT100: { code: 'FLAT100', label: '₹100 off orders above ₹499', min: 499, kind: 'flat', value: 100 },
    SAVE50:  { code: 'SAVE50',  label: '₹50 off any order',            min: 0,   kind: 'flat', value: 50 },
    FIRST20: { code: 'FIRST20', label: '20% off (max ₹200)',           min: 0,   kind: 'percent', value: 20, cap: 200 },
    GOLD10:  { code: 'GOLD10',  label: '10% off any order',            min: 0,   kind: 'percent', value: 10 },
  };
  const applyCoupon = (raw) => {
    const code = (raw || '').trim().toUpperCase();
    const c = COUPONS[code];
    if (!c) return { ok: false, msg: 'Invalid coupon code' };
    const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
    if (cartTotal < c.min) return { ok: false, msg: `Add ₹${c.min - cartTotal} more to use ${code}` };
    setCoupon({ code: c.code, label: c.label });
    return { ok: true, msg: `${code} applied — ${c.label}` };
  };
  const clearCoupon = () => setCoupon(null);
  const computeCouponDiscount = (subtotal) => {
    if (!coupon) return 0;
    const c = COUPONS[coupon.code];
    if (!c || subtotal < c.min) return 0;
    if (c.kind === 'flat') return Math.min(c.value, subtotal);
    const pct = Math.floor(subtotal * c.value / 100);
    return c.cap ? Math.min(pct, c.cap) : pct;
  };

  return (
    <AppContext.Provider value={{
      user, setUser, cart, addToCart, updateQty, removeFromCart, clearCart,
      bookings, placeBooking, refreshBookings,
      address, setAddress, role, setRole,
      login, signup, googleLogin, logout,
      categories, services, offers, users, loading, registerUser,
      upsertCategory, deleteCategory, upsertService, deleteService, upsertOffer, deleteOffer, resetAll,
      coupon, applyCoupon, clearCoupon, computeCouponDiscount, availableCoupons: Object.values(COUPONS),
      coins, setCoins, coinsRedeem, setCoinsRedeem,
      permissions, hasPermission, refreshPermissions,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
